import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, ShieldAlert } from 'lucide-react';
import { toast } from 'react-toastify';

import dashboardService from './api';
import TranscriptDisplay from './components/TranscriptDisplay';
import FraudTypeIdentifier from './components/FraudTypeIdentifier';
import QuestionSuggestions from './components/QuestionSuggestions';
import CRMUpdater from './components/CRMUpdater';
import CaseSummary from './components/CaseSummary';
import SentimentAnalysis from './components/SentimentAnalysis';
import CRMEntryUpdater from './components/CRMEntryUpdater';


const CustomerServiceDashboard = () => {
  const navigate = useNavigate();

  // Transcript state
  const [transcript, setTranscript] = useState('');
  const [callStatus, setCallStatus] = useState('idle'); // idle | active | ended
  const [fraudCollection, setFraudCollection] = useState('');

  // Widget results
  const [fraudResult, setFraudResult] = useState(null);
  const [questionsResult, setQuestionsResult] = useState(null);
  const [crmResult, setCrmResult] = useState(null);
  const [summaryResult, setSummaryResult] = useState(null);
  const [sentimentResult, setSentimentResult] = useState(null);

  // Loading states
  const [fraudLoading, setFraudLoading] = useState(false);
  const [questionsLoading, setQuestionsLoading] = useState(false);
  const [crmLoading, setCrmLoading] = useState(false);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [sentimentLoading, setSentimentLoading] = useState(false);
  const [isPushing, setIsPushing] = useState(false);

  // Error states
  const [fraudError, setFraudError] = useState(null);
  const [questionsError, setQuestionsError] = useState(null);
  const [crmError, setCrmError] = useState(null);
  const [summaryError, setSummaryError] = useState(null);
  const [sentimentError, setSentimentError] = useState(null);
  const [crmEntryResult, setCrmEntryResult] = useState(null);
  const [crmEntryLoading, setCrmEntryLoading] = useState(false);
  const [crmEntryError, setCrmEntryError] = useState(null);

  // Refs to hold latest values without stale-closure issues inside the interval
  const transcriptRef = useRef('');
  const callStatusRef = useRef('idle');
  const fraudCollectionRef = useRef('');
  const analysisIntervalRef = useRef(null);
  const isAnalyzingRef = useRef(false); // prevent overlapping calls
  const isFraudCallRef = useRef(false); // latches true once keywords detected, never resets mid-call

  // Last-good-result refs — keeps previous successful parse visible when next parse fails
  const lastFraudResultRef = useRef(null);
  const lastQuestionsResultRef = useRef(null);
  const lastCrmResultRef = useRef(null);
  const lastSummaryResultRef = useRef(null);
  const lastSentimentResultRef = useRef(null);

  // Keep refs in sync
  useEffect(() => { transcriptRef.current = transcript; }, [transcript]);
  useEffect(() => { callStatusRef.current = callStatus; }, [callStatus]);
  useEffect(() => { fraudCollectionRef.current = fraudCollection; }, [fraudCollection]);

  // ─── Core analysis function — reads from refs, safe inside intervals ───────

  const runAllAnalyses = useCallback(async () => {
    const text = transcriptRef.current;
    if (!text.trim() || isAnalyzingRef.current) return;
    isAnalyzingRef.current = true;

    // Keyword latch: once fraud keywords are detected, never flip back to false
    if (!isFraudCallRef.current) {
      isFraudCallRef.current = dashboardService.hasFraudKeywords(text);
    }
    const isFraudCall = isFraudCallRef.current;

    const collection = fraudCollectionRef.current || undefined;

    await Promise.all([
      (async () => {
        setFraudLoading(true);
        setFraudError(null);
        const r = await dashboardService.detectFraudType(text, collection, isFraudCall);
        setFraudLoading(false);
        const hasFraudData = r.success && !r.not_applicable && !!r.data?.primary_typology;
        if (hasFraudData) { lastFraudResultRef.current = r; setFraudResult(r); }
        else if (!lastFraudResultRef.current) {
          if (r.not_applicable) setFraudResult({ not_applicable: true });
          else if (!r.success) setFraudError(r.error || 'Failed to analyze fraud type');
        }
      })(),
      (async () => {
        setQuestionsLoading(true);
        setQuestionsError(null);
        const r = await dashboardService.suggestNextQuestions(text, '', isFraudCall);
        setQuestionsLoading(false);
        const hasQuestionsData = r.success && !r.not_applicable && !!r.data?.suggested_questions?.length;
        if (hasQuestionsData) { lastQuestionsResultRef.current = r; setQuestionsResult(r); }
        else if (!lastQuestionsResultRef.current) {
          if (r.not_applicable) setQuestionsResult({ not_applicable: true });
          else if (!r.success) setQuestionsError(r.error || 'Failed to get question suggestions');
        }
      })(),
      (async () => {
        setCrmLoading(true);
        setCrmError(null);
        const r = await dashboardService.extractCRMData(text, isFraudCall);
        setCrmLoading(false);
        const hasCrmData = r.success && !r.not_applicable && !!r.data?.required_fields;
        if (hasCrmData) { lastCrmResultRef.current = r; setCrmResult(r); }
        else if (!lastCrmResultRef.current) {
          if (r.not_applicable) setCrmResult({ not_applicable: true });
          else if (!r.success) setCrmError(r.error || 'Failed to extract CRM data');
        }
      })(),
      (async () => {
        setSummaryLoading(true);
        setSummaryError(null);
        const r = await dashboardService.generateCaseSummary(text, isFraudCall);
        setSummaryLoading(false);
        const hasSummaryData = r.success && !r.not_applicable && !!r.data?.executive_summary;
        if (hasSummaryData) { lastSummaryResultRef.current = r; setSummaryResult(r); }
        else if (!lastSummaryResultRef.current) {
          if (r.not_applicable) setSummaryResult({ not_applicable: true });
          else if (!r.success) setSummaryError(r.error || 'Failed to generate summary');
        }
      })(),
      (async () => {
        setSentimentLoading(true);
        setSentimentError(null);
        const r = await dashboardService.analyzeSentiment(text, isFraudCall);
        setSentimentLoading(false);
        const hasSentimentData = r.success && !r.not_applicable && !!r.data?.overall_sentiment;
        if (hasSentimentData) { lastSentimentResultRef.current = r; setSentimentResult(r); }
        else if (!lastSentimentResultRef.current) {
          if (r.not_applicable) setSentimentResult({ not_applicable: true });
          else if (!r.success) setSentimentError(r.error || 'Failed to analyze sentiment');
        }
      })(),
    ]);

    isAnalyzingRef.current = false;
  }, []); // stable — reads from refs only

  // ─── Start/stop the 3-second analysis interval based on call status ────────

  useEffect(() => {
    if (callStatus === 'active') {
      // Run immediately on call start, then every 3s
      runAllAnalyses();
      analysisIntervalRef.current = setInterval(runAllAnalyses, 3000);
    } else {
      clearInterval(analysisIntervalRef.current);
      analysisIntervalRef.current = null;
      // Run one final pass when call ends
      if (callStatus === 'ended') runAllAnalyses();
    }
    return () => clearInterval(analysisIntervalRef.current);
  }, [callStatus, runAllAnalyses]);

  // ─── Event handlers ────────────────────────────────────────────────────────

  const handleTranscriptionUpdate = useCallback((text) => {
    setTranscript(text);
  }, []);

  const handleCallStatusChange = useCallback((status) => {
    setCallStatus(status);
    if (status === 'active') {
      // Reset everything for the new call
      dashboardService.clearRAGCache();
      isAnalyzingRef.current = false;
      isFraudCallRef.current = false;
      lastFraudResultRef.current = null;
      lastQuestionsResultRef.current = null;
      lastCrmResultRef.current = null;
      lastSummaryResultRef.current = null;
      lastSentimentResultRef.current = null;
      setTranscript('');
      transcriptRef.current = '';
      setFraudResult(null);
      setQuestionsResult(null);
      setCrmResult(null);
      setSummaryResult(null);
      setSentimentResult(null);
      setFraudError(null);
      setQuestionsError(null);
      setCrmError(null);
      setSummaryError(null);
      setSentimentError(null);
    }
  }, []);

  const handleGenerateCRMEntry = useCallback(async () => {
    if (!transcript.trim()) return;
    setCrmEntryLoading(true);
    setCrmEntryError(null);
    const result = await dashboardService.generateCRMEntry(transcript);
    setCrmEntryLoading(false);
    if (result.success) {
      setCrmEntryResult(result);
    } else {
      setCrmEntryError(result.error || 'Failed to generate CRM entry');
    }
  }, [transcript]);

  const handlePushToCRM = useCallback(async () => {
    if (!crmResult?.data) return;
    setIsPushing(true);
    const result = await dashboardService.pushToCRM({
      ...crmResult.data,
      fraud_type: fraudResult?.data?.primary_typology?.name,
      fraud_confidence: fraudResult?.data?.primary_typology?.confidence,
      transcript_excerpt: transcript.slice(-500)
    });
    setIsPushing(false);
    if (result.success) {
      toast.success('Claim data pushed to CRM successfully');
    } else {
      toast.error(`CRM push failed: ${result.error}`);
    }
  }, [crmResult, fraudResult, transcript]);

  return (
    <div className="h-screen bg-gradient-to-br from-gray-50 via-[#000080]/5 to-[#000080]/[0.03] flex flex-col overflow-hidden">
      {/* Page Header — matches AgentMarketplace style */}
      <div className="relative px-6 pt-6 pb-0">
        <div className="relative bg-white/90 backdrop-blur-xl p-4 sm:p-5 rounded-2xl overflow-hidden border border-gray-200 shadow-sm">
          <div className="absolute inset-0 bg-gradient-to-br from-[#000080]/5 to-[#000080]/10 opacity-70 pointer-events-none" />
          <div className="relative flex items-center justify-between gap-3">
            <div className="flex items-center gap-3 sm:gap-4">
              <button
                onClick={() => navigate('/home')}
                className="text-gray-700 hover:text-gray-900 hover:bg-gray-100 p-2 rounded-xl transition-all duration-200 flex-shrink-0"
                aria-label="Back to home"
              >
                <ChevronLeft className="w-5 h-5 sm:w-6 sm:h-6" />
              </button>
              <div className="flex items-center gap-3">
                
                <div>
                  <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">
                    Customer Service Dashboard
                  </h1>
                  <p className="text-gray-600 text-xs sm:text-sm mt-0.5">
                    Fraud claim intake - AI-assisted real-time analysis
                  </p>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-3 flex-shrink-0">
              {/* Call status — only shown when a call is in progress or has ended */}
              {callStatus !== 'idle' && (
                <span className={`flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-full border ${
                  callStatus === 'active'
                    ? 'bg-green-50 text-green-700 border-green-200'
                    : 'bg-gray-100 text-gray-600 border-gray-200'
                }`}>
                  <span className={`w-2 h-2 rounded-full ${callStatus === 'active' ? 'bg-green-500 animate-pulse' : 'bg-gray-400'}`} />
                  {callStatus === 'active' ? 'Call Active' : 'Call Ended'}
                </span>
              )}

              {/* Partner logos */}
              <div className="flex items-center gap-3 pl-1">
                <img src="/NFCU-logo.png" alt="Navy Federal Credit Union" className="h-8 object-contain" />
                <div className="w-px h-6 bg-gray-200" />
                <img src="/capgemini-logo.png" alt="Capgemini" className="h-5 object-contain" />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Split-screen body — fills remaining viewport height */}
      <div className="flex flex-1 overflow-hidden mt-4 mx-6 mb-6 gap-4 min-h-0">
        {/* LEFT — Live Transcription */}
        <div className="w-1/3 flex flex-col min-h-0">
          <TranscriptDisplay
            onTranscriptionUpdate={handleTranscriptionUpdate}
            onCallStatusChange={handleCallStatusChange}
          />
        </div>

        {/* RIGHT — Intelligence Widgets */}
        <div className="w-2/3 overflow-y-auto space-y-3 pb-2 [&::-webkit-scrollbar]:w-2
          + [&::-webkit-scrollbar-track]:bg-gray-50 pr-2 [&::-webkit-scrollbar-                   
          +thumb]:bg-gray-300 [&::-webkit-scrollbar-thumb]:rounded-full">
          {/* Widget 1: Fraud Type Identifier */}
          <FraudTypeIdentifier
            result={fraudResult}
            isLoading={fraudLoading}
            error={fraudError}
            onRefresh={runAllAnalyses}
            onCollectionChange={setFraudCollection}
          />

          {/* Widget 2: Next Question Suggestions */}
          <QuestionSuggestions
            result={questionsResult}
            isLoading={questionsLoading}
            error={questionsError}
            onRefresh={runAllAnalyses}
          />

          {/* Widget 3: CRM Auto-Updater */}
          {/* <CRMUpdater
            result={crmResult}
            isLoading={crmLoading}
            error={crmError}
            onRefresh={runAllAnalyses}
            onPushToCRM={handlePushToCRM}
            isPushing={isPushing}
          /> */}


          {/* Widget 5: Case Summary */}
          <CaseSummary
            result={summaryResult}
            isLoading={summaryLoading}
            error={summaryError}
            onRefresh={runAllAnalyses}
          />

          {/* Widget 4: Sentiment Analysis */}
          <SentimentAnalysis
            result={sentimentResult}
            isLoading={sentimentLoading}
            error={sentimentError}
            onRefresh={runAllAnalyses}
          />

          {/* Widget 6: CRM Entry Updater */}
          <CRMEntryUpdater
            transcript={transcript}
            callStatus={callStatus}
            onGenerate={handleGenerateCRMEntry}
            result={crmEntryResult}
            isLoading={crmEntryLoading}
            error={crmEntryError}
          />

        </div>
      </div>
    </div>
  );
};

export default CustomerServiceDashboard;
